import React, { useState, useEffect, useRef, useCallback } from "react";
import { withStyles, Grow } from "@material-ui/core";
import { withTranslation } from "react-i18next";
import { CheckCircleOutline, Close } from "@material-ui/icons";
import Webcam from "react-webcam";
import { pushNotification } from "../../actions/index";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";

// Tools
import { getBase64 } from "../../utils/tools";
import { images } from "../../utils/images";

function TwoSidedID(props) {
  const { classes, t, method, setPhotos } = props;
  const webcamRef = useRef(null);
  const [step, changeStep] = useState(1);
  const [cameraReady, setCameraReady] = useState(false);

  // Camera Files
  const [cameraActive, activateCamera] = useState(false);
  const [currentPhoto, setCurrentPhoto] = useState("");
  const [frontOfIDCamera, setIdFrontCamera] = useState("");
  const [backOfIDCamera, setIdBackCamera] = useState("");

  // Uploaded Files
  const [currentPhotoUploaded, setCurrentPhotoUploaded] = useState({
    photoUrl: "",
    file: null,
  });
  const [frontOfIDUploaded, setFrontIdUploaded] = useState("");
  const [backOfIDUploaded, setBackIdUploaded] = useState("");
  const [cameraAllowed, setCameraAllowed] = useState(true);

  useEffect(() => {
    if (method === null || props.restart) {
      setCurrentPhoto("");
      setIdBackCamera("");
      setIdFrontCamera("");
      activateCamera(false);
      changeStep(1);
    }
  }, [method, props.restart]);

  // Capture Photo
  const capturePhoto = useCallback(() => {
    let cam = document.getElementsByTagName("video");

    const imageSrc = webcamRef.current.getScreenshot({
      height: cam[0].offsetHeight * 4,
      width: cam[0].offsetWidth * 4,
    });
    setCurrentPhoto(imageSrc);
  }, [webcamRef]);

  // Delete current photo
  const retakePhoto = () => {
    setCurrentPhoto("");
  };

  // No Camera Permission
  const handleNoCameraPermission = () => {
    setCameraAllowed(false);
  };

  // Confirm current photo
  const confirmPhoto = () => {
    if (step === 1) {
      setIdFrontCamera(currentPhoto);
      changeStep(2);
      activateCamera(false);
      setCurrentPhoto("");
    } else {
      setIdBackCamera(currentPhoto);
      activateCamera(false);
      setCurrentPhoto("");
      setPhotos({
        idFront: frontOfIDCamera,
        idBack: currentPhoto,
      });
    }
  };

  ///////////////////////////// UPLOADS METHODS //////////////////////////////
  // Handle Image Uplaoded
  const imageUploaded = (file) => {
    let acceptedTypes = ["image/png", "image/jpeg", "image/jpg"];
    if (!acceptedTypes.includes(file.type)) {
      props.pushNotification({ message: "_invalidFileType", type: "error" });
      return;
    }

    if (file.size > 10485760) {
      props.pushNotification({ message: "_fileTooBig", type: "error" });
      return;
    }

    const photo = URL.createObjectURL(file);
    setCurrentPhotoUploaded({ photoUrl: photo, file: file });
  };

  // Create File Input Selector
  const createFileSelector = () => {
    const fileSelector = document.createElement("input");
    fileSelector.setAttribute("type", "file");
    fileSelector.setAttribute("accept", ".png, .jpg");
    fileSelector.addEventListener(
      "change",
      function (e) {
        imageUploaded(e.target.files[0]);
      },
      false
    );
    return fileSelector;
  };

  const fileInput = createFileSelector();

  // Browse Files
  const browseFiles = () => {
    fileInput.click();
  };

  // Remove Uploaded Photo
  const removeUploadedPhoto = () => {
    setCurrentPhotoUploaded({ photoUrl: "", file: null });
    fileInput.value = "";
  };

  // Confirm Uploaded Photo
  const confirmPhotoUploaded = async () => {
    let base64 = await getBase64(currentPhotoUploaded.file);

    if (step === 1) {
      setFrontIdUploaded(base64);
      changeStep(2);
      setCurrentPhotoUploaded({ photoUrl: "", file: null });
      fileInput.value = "";
    } else {
      setBackIdUploaded(base64);
      setCurrentPhotoUploaded({ photoUrl: "", file: null });
      fileInput.value = "";
      setPhotos({
        idFront: frontOfIDUploaded,
        idBack: base64,
      });
    }
  };

  if (method === null) {
    return <span />;
  }

  if (method === "upload") {
    return (
      <div className={classes.twoSided}>
        {currentPhotoUploaded.photoUrl !== "" ? (
          <div className="previewUpload">
            <img
              src={currentPhotoUploaded.photoUrl}
              style={{
                width: "auto",
                height: "auto",
                maxWidth: "100%",
                maxHeight: 400,
                marginBottom: 8,
              }}
            />
            <div className="uploadedPhotoBtns">
              <button onClick={() => removeUploadedPhoto()}>
                {t("removePhoto")}
              </button>
              <button
                className="confirmBtn"
                onClick={() => confirmPhotoUploaded()}
              >
                {t("confirm")}
              </button>
            </div>
          </div>
        ) : (
          <div className="instructionsDiv">
            <div className="title">
              <h2>{step === 1 ? t("frontDocument") : t("backDocument")}</h2>
              <p>
                <span>{step}</span>/2
              </p>
            </div>
            <p className="sublabel">{t("idUploadInstructions")}</p>
            <img
              className="exampleImage"
              src={
                step === 1
                  ? images.illustrations.idFront
                  : images.illustrations.idBack
              }
            />
            <button onClick={() => browseFiles()} className="nextButton">
              {t("browseFiles")}
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={classes.twoSided}>
      {cameraActive ? (
        currentPhoto === "" ? (
          <Webcam
            audio={false}
            width={"100%"}
            ref={webcamRef}
            screenshotFormat="image/jpeg"
            screenshotQuality={1}
            onUserMedia={() => setCameraReady(true)}
            style={{
              border: "1px solid black",
              borderRadius: 2,
            }}
            onUserMediaError={(e) => handleNoCameraPermission()}
          />
        ) : (
          <img
            src={currentPhoto}
            style={{ width: "100%", height: "auto", objectFit: "contain" }}
          />
        )
      ) : (
        <div className="instructionsDiv">
          <div className="title">
            <h2>{step === 1 ? t("frontDocument") : t("backDocument")}</h2>
            <p>
              <span>{step}</span>/2
            </p>
          </div>
          <p className="sublabel">
            {step === 1 ? t("idFrontInstructions") : t("idBackInstructions")}
          </p>
          <img
            className="exampleImage"
            src={
              step === 1
                ? images.illustrations.idFront
                : images.illustrations.idBack
            }
          />
          <button onClick={() => activateCamera(true)} className="nextButton">
            {t("takePhoto")}
          </button>
        </div>
      )}
      {cameraActive ? (
        <div className="cameraButtons">
          {currentPhoto === "" ? (
            cameraAllowed ? (
              <div className="photoBtnWrapper">
                <div
                  className="takePhotoBtn"
                  onClick={
                    cameraAllowed && cameraReady
                      ? () => capturePhoto()
                      : () => {}
                  }
                />
              </div>
            ) : (
              <p className={classes.cameraNotAllowed}>{t("pleaseAllow")}</p>
            )
          ) : (
            <div className="takenBtnWrapper">
              <button
                className={classes.retakeBtn}
                onClick={() => retakePhoto()}
              >
                {t("retakePhoto")}
              </button>
              <button className="confirmBtn" onClick={() => confirmPhoto()}>
                {t("confirm")}
              </button>
            </div>
          )}
        </div>
      ) : (
        ""
      )}
    </div>
  );
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({ pushNotification }, dispatch);
}

const styles = (theme) => ({
  retakeBtn: {
    color: theme.colors.misc.black,
  },
  cameraNotAllowed: {
    color: theme.colors.buttons.sell,
    marginTop: 16,
    // fontFamily: "Titillium Web",
    lineHeight: "21px",
    fontSize: 14,
  },
  twoSided: {
    height: "100%",
    "& .previewUpload": {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      height: "100%",
      "& .uploadedPhotoBtns": {
        display: "flex",
        justifyContent: "center",
        marginTop: "auto",
        "& button": {
          ...theme.defaults.roundBtn,
          color: theme.colors.misc.black,
          "&.confirmBtn": {
            color: theme.colors.text.primary,
            background: theme.colors.buttons.green,
            marginLeft: 8,
          },
        },
      },
    },

    "& .instructionsDiv": {
      display: "flex",
      height: "100%",

      flexDirection: "column",
      "& .title": {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-end",
        width: "100%",
        fontSize: 18,
        marginBottom: 8,
        // fontFamily: "Titillium Web",
        "& h2": {
          marginBottom: 0,
        },

        "& p": {
          color: theme.colors.buttons.green,
          fontWeight: 600,
          "& span": {
            fontSize: 32,
          },
        },
      },
      "& .sublabel": {
        // fontFamily: "Titillium Web",
        color: theme.colors.text.secondary,
        fontSize: 14,
        lineHeight: "21px",
      },
      "& .exampleImage": {
        width: "60%",
        height: "auto",
        margin: "32px auto 24px",
        display: "block",
      },
      "& button": {
        ...theme.defaults.roundBtn,
        background: theme.colors.buttons.green,
        alignSelf: "flex-end",
        marginTop: "auto",
        fontSize: 14,
      },
    },
  },
});

export default withStyles(styles)(
  withTranslation()(connect(null, mapDispatchToProps)(TwoSidedID))
);
